export interface Account {
	portal_id: string;
	name: string;
	logo: string;
	logo_link?: string;
	domain: string;
}

export type PortalHomepage = {
	homepage: string;
};

export interface UserAccount {
	id: string;
	label: string;
}

export interface IdentityUserAccount {
	account_id: string;
	name: string;
	label: string;
}

export interface UserData {
	id: string;
	name: string;
	avatar_url: string | null;
	preferred_language: string;
	accounts: IdentityUserAccount[];
	public_user: string;
}

export interface ILogo {
	linkUrl: string;
	imageUrl: string;
}

export interface NavItem {
	id: string;
	url: string;
	label: string;
}

export interface PresetItem {
	id: string;
	name: string;
	type: 'campaign' | 'job';
}

export interface Language {
	locale: string;
	display_name: string;
}

export interface HeaderInfo {
	general: {
		accountId: string;
		accountName: string;
		portalDomain: string;
		isLoggedIn: boolean;
		userId: string;
		userName: string;
		userPictureUrl: string | null;
		isPublic: boolean;
	};

	languages: {
		available: Language[];
		selected: string;
	};

	accounts: IdentityUserAccount[];

	logo: ILogo;

	navItems: NavItem[];

	primaryCTA: {
		id: 'uploadMedia' | 'convertToPDF';
		url: string;
	};

	lastChange: Date;
	setLastChange: () => void;

	homepage: {
		isEnabled: boolean | null;
		isConfigured: boolean | null;
		isSplitEnabled: boolean | null;
		isAppAccessEnabled: boolean | null;
	};
}

export interface CartItem {
	amount: string;
	price: string;
	name: string;
	hasAssets: boolean;
	assetThumb: string;
	uploadedAssetThumb: string;
}

export interface Cart {
	items: CartItem[];
	isVATEnabled: boolean;
	totalPrice: string;
	isCartAvailable: boolean;
}

export interface SearchResultTag {
	id: string;
	tagName: string;
}

export interface MetapropertiesOption {
	id: string;
	label: string;
	name: string;
	image?: string;
	description?: string;
	[key: string]: unknown;
}

export interface SearchMetaproperty {
	label: string;
	name: string;
	options?: MetapropertiesOption[];
	orderBy?: 'label' | 'zindex';
	sortType?: 'textnocase' | 'numeric';
	spotlightFilterAction?: string;
}

export interface SearchMetapropertyResponse extends SearchMetaproperty {
	id: string;
}

export interface SearchItem {
	id: string;
	name: string;
	idHash?: string;
	description?: string;
	title?: string;
	tags?: string[];
	// We only need both assetCount ans mediaCount while migrating to the new search endpoints
	// After that, we can remove mediaCount
	mediaCount?: number;
	assetCount?: number;
	extension?: string[];
	thumbnails?: {
		large: string;
		mini: string;
	};
	thumbnailUrl?: string; // for collections endpoint v3
	path?: string; // for collections endpoint v3
}

export interface Guideline {
	title: string;
	path: string;
	image: string;
	iconSvg: string;
}

export type Highlight = Record<
	string,
	{
		description?: string[];
		copyright?: string[];
		tags?: string[];
		fileContent?: string[];
		transcription?: string[];
		textFromImage?: string[];
	}
>;

type SearchSpotlightCollection = {
	assetCount: number;
	id: string;
	name: string;
	thumbnailUrl: string; // eg "/asset/transform/123/"
	path: string; // eg "/collections/view/E83B782B-08FB-43FB-A76E214742897357"
};

export type SpotlightMediaSearchResponse = {
	count: number;
	results: SearchItem[];
	tags: string[] | number[];
	highlighting?: Highlight;
};

export type SpotlightCollectionsSearchResponse = {
	count: number;
	results: SearchSpotlightCollection[];
};

export type SpotlightMediaSearchInCollectionResponse = {
	results: {
		media: {
			docs: SearchItem[];
			numFound: number;
		};
	};
	highlighting?: Highlight;
	tags: string[] | number[];
};

export type SpotlightMetapropertiesSearchResponse = {
	count: number;
	results: SearchMetaproperty[];
};

export type BrandStoreAndStyleGuideResponse = {
	count: number;
	results: SearchItem[];
};

export type SpotlightSearchParams = {
	recipientShareId?: string | undefined;
	filterKey: string;
	searchType: string;
	collectionId: string;
};

export type SearchResponse = {
	results: {
		media: { docs: SearchItem[]; numFound: number };
		collection: { docs: SearchItem[]; numFound: number };
		page: { docs: SearchItem[]; numFound: number };
		product: { docs: SearchItem[]; numFound: number };
	};
	savedFilters: {
		accountId: string;
		audit: number;
		filterIsActive: boolean;
		metaProperties: Record<string, string>;
		orderBy: string; // Example: "datePublished desc, dateCreated desc"
		orderType: 'desc' | 'asc';
		searchmode: string; // "keyword" | ...
		singletext: string; // The search term typed by the user
		viewType: 'grid' | 'list';
	};
	metaproperties: { [id: string]: SearchMetapropertyResponse };
	tags: string[] | number[];
	filterKey: string;
	highlighting?: Highlight;
};

export type SearchResults = {
	assets: { items: SearchItem[]; total: number };
	collections: { items: SearchItem[]; total: number };
	pages: { items: SearchItem[]; total: number };
	guidelines: { items: Guideline[]; total: number };
	metaproperties: SearchMetaproperty[];
	tags: string[] | number[];
};

interface ItemProps {
	isOpen: boolean;
}

/**
 * componentClassName: use if the menu item component has a className in the parent wrapper (li)
 * componentIsButton: if true the item component will be treated as a menu item without sub items
 */
export interface MenuItem {
	key?: string;
	isVisible: boolean;
	label: string;
	icon?: React.ComponentType<{ size: string }>;
	href?: string;
	menuItems?: MenuItem[];
	Component?: (props: ItemProps) => React.JSX.Element;
	componentClassName?: string;
	componentIsButton?: boolean;
	badgeLabel?: string;
	target?: string;
}

export type Actions = Rule['action'];
export type Subjects = Rule['subject'];

export type Rules = Rule[];

export type Rule =
	| { action: 'superadmin'; subject: 'all' }
	| { action: 'manage'; subject: 'media_brand_news' }
	| { action: 'admin'; subject: 'all' }
	| { action: 'show'; subject: 'notifications' }
	| { action: 'show'; subject: 'notifications_center' }
	| { action: 'show'; subject: 'public_filters' }
	| { action: 'show'; subject: 'brandstore' }
	| { action: 'show'; subject: 'account_switcher' }
	| { action: 'show'; subject: 'cta_button_redirect' }
	| { action: 'show'; subject: 'user_settings_menu' }
	| { action: 'show'; subject: 'user_outofoffice' }
	| { action: 'show'; subject: 'account_settings_menu' }
	| { action: 'show'; subject: 'upload_media' }
	| { action: 'show'; subject: 'access_workspace' }
	| { action: 'show'; subject: 'workflow_create_new' }
	| { action: 'show'; subject: 'workflow_add_preset' }
	| { action: 'show'; subject: 'task_external_upload_audit' }
	| { action: 'show'; subject: 'workflow_job_started_from_brandstore' }
	| { action: 'show'; subject: 'workflow_campaign_overview_homepage' }
	| { action: 'show'; subject: 'exit_impersonate' }
	| { action: 'show'; subject: 'sticky_search' }
	| { action: 'show'; subject: 'login_sso' }
	| { action: 'show'; subject: 'pylogin' }
	| { action: 'show'; subject: 'pynder_logout' }
	| { action: 'show'; subject: 'user_request' }
	| { action: 'show'; subject: 'dashboard_search' }
	| { action: 'show'; subject: 'spotlight_recent_searches' }
	| { action: 'show'; subject: 'spotlight_help_modal' }
	| { action: 'show'; subject: 'media_overview_role' }
	| { action: 'show'; subject: 'media_asset_bank' }
	| { action: 'show'; subject: 'improved_taxonomy_management' }
	| { action: 'show'; subject: 'brandguidelines' }
	| { action: 'show'; subject: 'homepage' }
	| { action: 'show'; subject: 'ai_similarity_search' }
	| { action: 'show'; subject: 'custom_links_overview' }
	| { action: 'select'; subject: 'menu_item_edit_profile' }
	| { action: 'select'; subject: 'menu_item_fte_management' }
	| { action: 'select'; subject: 'menu_item_bare_uploader' }
	| { action: 'select'; subject: 'menu_item_version_updater' }
	| { action: 'select'; subject: 'menu_item_transfer' }
	| { action: 'select'; subject: 'asmi_file_service_migration' }
	| { action: 'select'; subject: 'asmi_waitingroom' }
	| { action: 'select'; subject: 'asmi_look_and_feel' }
	| { action: 'select'; subject: 'asmi_news_management' }
	| { action: 'select'; subject: 'asmi_general_quicklinks' }
	| { action: 'select'; subject: 'asmi_manage_brands' }
	| { action: 'select'; subject: 'asmi_manage_category' }
	| { action: 'select'; subject: 'asmi_manage_homepage' }
	| { action: 'select'; subject: 'asmi_manage_homepage_temp' }
	| { action: 'select'; subject: 'asmi_manage_metaproperties' }
	| { action: 'select'; subject: 'asmi_manage_smartfilters' }
	| { action: 'select'; subject: 'asmi_manage_tags' }
	| { action: 'select'; subject: 'asmi_manage_analytics' }
	| { action: 'select'; subject: 'asmi_manage_changehistory' }
	| { action: 'select'; subject: 'asmi_manage_user_requests' }
	| { action: 'select'; subject: 'asmi_manage_user_requests_v2' }
	| { action: 'select'; subject: 'asmi_manage_users' }
	| { action: 'select'; subject: 'asmi_manage_profiles' }
	| { action: 'select'; subject: 'asmi_manage_groups' }
	| { action: 'select'; subject: 'asmi_manage_workflow' }
	| { action: 'select'; subject: 'asmi_manage_workflow_presets' }
	| { action: 'select'; subject: 'asmi_manage_workflow_metaproperties' }
	| { action: 'select'; subject: 'asmi_manage_workflow_fte' }
	| { action: 'select'; subject: 'asmi_manage_workflow_group' }
	| { action: 'select'; subject: 'asmi_manage_workflow_group_v2' }
	| { action: 'select'; subject: 'asmi_manage_workflow_campaign' }
	| { action: 'select'; subject: 'asmi_manage_workflow_role' }
	| { action: 'select'; subject: 'asmi_manage_templates' }
	| { action: 'select'; subject: 'asmi_manage_templates_access' }
	| { action: 'select'; subject: 'asmi_manage_templates_variables' }
	| { action: 'select'; subject: 'asmi_advaced_settings' }
	| { action: 'select'; subject: 'asmi_trash' }
	| { action: 'select'; subject: 'asmi_cockpit' }
	| { action: 'select'; subject: 'workflow_add_asset' }
	| { action: 'select'; subject: 'workflow_add_asset_template' }
	| { action: 'select'; subject: 'workflow_add_task' }
	| { action: 'select'; subject: 'workflow_add_stage' }
	| { action: 'select'; subject: 'create_new_brand' }
	| { action: 'select'; subject: 'add_news' }
	| { action: 'select'; subject: 'media_find_duplicates' }
	| { action: 'select'; subject: 'workflow_add_campaign' }
	| { action: 'select'; subject: 'workflow_add_job' }
	| { action: 'select'; subject: 'workflow_use_preset' }
	| { action: 'select'; subject: 'workflow_add_preset' }
	| { action: 'select'; subject: 'quarantine' }
	| { action: 'manage'; subject: 'bynder_ai_agents' }
	| { action: 'run'; subject: 'bynder_ai_agents' };

export type TreatmentKeys =
	| 'portal_bynder_express'
	| 'portal_new_homepage_rollout'
	| 'workspace'
	| 'spotlight_new_search_endpoints'
	| 'share_custom_links'
	| 'ai_agents'
	| 'assetbank_nls_on_dashboard'
	| 'analytics_dam_governance';

export enum TypesOfSpotlightRequests {
	Assets = 'assets',
	Collections = 'collections',
	Metaproperties = 'metaproperties',
	StyleGuidePages = 'styleGuidePages',
	Guidelines = 'guidelines',
}
